import React from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../tokens';

const cardVariants = {
  default: css`
    background: ${theme.colors.background.primary};
    border: 1px solid ${theme.colors.border.light};
  `,
  elevated: css`
    background: ${theme.colors.background.elevated};
    border: none;
    box-shadow: ${theme.shadows.md};
  `,
  outlined: css`
    background: transparent;
    border: 1px solid ${theme.colors.border.default};
  `,
  filled: css`
    background: ${theme.colors.background.secondary};
    border: none;
  `,
  gradient: css`
    background: ${theme.gradients.light};
    border: none;
  `,
  premium: css`
    background: ${theme.colors.background.primary};
    border: 2px solid ${theme.colors.premium[200]};
    box-shadow: ${theme.shadows.premium};
  `,
};

const cardSizes = {
  sm: css`
    padding: ${theme.spacing[4]};
    border-radius: ${theme.radii.lg};
  `,
  md: css`
    padding: ${theme.spacing[6]};
    border-radius: ${theme.radii.xl};
  `,
  lg: css`
    padding: ${theme.spacing[8]};
    border-radius: ${theme.radii['2xl']};
  `,
  none: css`
    padding: 0;
    border-radius: ${theme.radii.xl};
  `,
};

const StyledCard = styled.div`
  ${({ $variant }) => cardVariants[$variant] || cardVariants.default}
  ${({ $size }) => cardSizes[$size] || cardSizes.md}

  ${({ $hoverable }) =>
    $hoverable &&
    css`
      cursor: pointer;
      transition: ${theme.transitions.card};

      &:hover {
        transform: translateY(-2px);
        box-shadow: ${theme.shadows.cardHover};
      }
    `}

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;

      &:hover {
        border-color: ${theme.colors.primary[300]};
      }

      &:active {
        transform: scale(0.99);
      }
    `}
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};

  ${({ $borderBottom }) =>
    $borderBottom &&
    css`
      padding-bottom: ${theme.spacing[4]};
      border-bottom: 1px solid ${theme.colors.border.light};
    `}
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  line-height: ${theme.lineHeights.tight};
`;

const CardSubtitle = styled.p`
  margin: ${theme.spacing[1]} 0 0 0;
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const CardBody = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: ${theme.lineHeights.relaxed};
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'left' ? 'flex-start' : $align === 'right' ? 'flex-end' : 'space-between'};
  gap: ${theme.spacing[3]};
  margin-top: ${theme.spacing[4]};

  ${({ $borderTop }) =>
    $borderTop &&
    css`
      padding-top: ${theme.spacing[4]};
      border-top: 1px solid ${theme.colors.border.light};
    `}
`;

const CardImage = styled.div`
  margin: ${({ $fullWidth }) => ($fullWidth ? `-${theme.spacing[6]}` : '0')};
  margin-bottom: ${theme.spacing[4]};
  border-radius: ${({ $fullWidth }) => ($fullWidth ? `${theme.radii.xl} ${theme.radii.xl} 0 0` : theme.radii.lg)};
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
`;

export const Card = React.forwardRef(
  ({ children, variant = 'default', size = 'md', hoverable = false, clickable = false, as, ...props }, ref) => {
    return (
      <StyledCard
        ref={ref}
        as={as}
        $variant={variant}
        $size={size}
        $hoverable={hoverable}
        $clickable={clickable}
        {...props}
      >
        {children}
      </StyledCard>
    );
  }
);

Card.displayName = 'Card';
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Image = CardImage;

export default Card;
